import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import FolderIcon from "@mui/icons-material/FolderSpecialOutlined"
import CalendarIcon from "@mui/icons-material/CalendarTodayOutlined"
import PersonIcon from "@mui/icons-material/PersonOutline"
import CopyIcon from "@mui/icons-material/ContentCopyOutlined"

import { projectApi, clientApi, userApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import ProjectDialog from "./projects/ProjectDialog"
import CopyProjectDialog from "./projects/CopyProjectDialog"
import { avatarColor, formatDate, initialsOf, isOverdue } from "../utils/format"
import { INTERACTIVE_CARD, rise } from "../motion"
import { filledChipSx } from "../theme/contrast"
import { BRAND_FILL } from "../theme"
import ClientSelect from "../components/ClientSelect"
import PersonSelect from "../components/PersonSelect"
import QuickFilters from "../components/QuickFilters"
import SavedViews from "../components/SavedViews"

const STATUS_COLOR = { planning: "info", active: "primary", on_hold: "warning", completed: "success", archived: "default" }

/*
 * Deliberately not the same question as Owner = me.
 *
 * A project you were added to the team of is yours to look at even when
 * somebody else owns it, and a view called "My projects" that hid those would
 * be wrong in the direction that loses work.
 */
const PROJECT_VIEWS = [{ key: "mine", label: "My projects", icon: PersonIcon }]

/* What "no filters" looks like, so a saved view can be told from the page you
   are already on. Saving the default state produces a bookmark that does
   nothing, and a list of those is worse than no list. */
const PROJECT_FILTER_DEFAULTS = { client_id: "", owner_id: "", mine: false, q: "" }

export default function ProjectsPage() {
  const { can, isBackOffice } = usePermissions()
  const navigate = useNavigate()

  const [projects, setProjects] = useState([])
  const [clients, setClients] = useState([])
  /* Seeded from ?client_id=, so "12 more projects" on a client's card lands on
     exactly those twelve rather than on everything with a filter to set. */
  const [clientFilter, setClientFilter] = useState(() => new URLSearchParams(window.location.search).get("client_id") || "")
  const [ownerFilter, setOwnerFilter] = useState("")
  const [mine, setMine] = useState(false)
  const [people, setPeople] = useState([])
  const [search, setSearch] = useState("")
  /* Two different numbers. `total` is how many match what is being asked for;
     `overall` is how many there are altogether, which is what makes "3 of 64" a
     sentence somebody can act on rather than a number with no scale. */
  const [total, setTotal] = useState(null)
  const [overall, setOverall] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [copying, setCopying] = useState(null)

  const filtersActive = Boolean(clientFilter) || Boolean(ownerFilter) || mine || Boolean(search.trim())

  const clearFilters = () => {
    setClientFilter("")
    setOwnerFilter("")
    setMine(false)
    setSearch("")
  }

  /*
   * The directory is served a page at a time, so the picker asks for more as
   * somebody types rather than filtering the page it happens to hold.
   *
   * This is also what loads the first page: the picker calls it once on mount
   * with an empty query, so a separate effect doing the same fetch would only
   * be a second identical request. `useCallback` because SearchSelect debounces
   * on this function's identity, and a new one each render restarts the timer
   * forever.
   *
   * `/users/directory` rather than `/users`: every signed-in account can read
   * it, where listing users needs a permission a client member does not have —
   * and they still need to filter their own team's work by who is on it.
   */
  const searchPeople = useCallback((query) => {
    userApi
      .directory(query || undefined)
      .then((data) => setPeople(data.users || []))
      .catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (clientFilter) params.client_id = clientFilter
      if (ownerFilter) params.owner_id = ownerFilter
      if (mine) params.mine = "true"
      if (search.trim()) params.q = search.trim()
      const data = await projectApi.list(params)
      const list = data.projects || []
      setProjects(list)
      /*
       * The count comes from the server, not from `list.length`.
       *
       * The list is one page of at most fifty. Counting the rows that came back
       * and calling it the total tells somebody with sixty projects that they
       * have fifty, and nothing else on the screen could contradict it.
       */
      const matching = typeof data.total === "number" ? data.total : list.length
      setTotal(matching)
      if (!filtersActive) setOverall(matching)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [clientFilter, ownerFilter, mine, search, filtersActive])

  useEffect(() => {
    // Debounced: typing in the search box should not be one request per keystroke.
    const timer = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  useEffect(() => {
    if (!isBackOffice || !can("clients", "view")) return
    clientApi
      .list()
      .then((data) => setClients(data.organizations || []))
      .catch(() => setClients([]))
  }, [isBackOffice, can])


  return (
    <Box>
      <PageHeader
        title="Projects"
        subtitle="Work grouped by what it is for. A project's tasks, members and conversations live together."
        actions={
          can("projects", "create") ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New project
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {/* A filter that floats loose above a grid of cards reads as part of the
          grid. In a bordered row it reads as a control over it — and the row has
          somewhere to put the count and the way back out. The row is here for
          everybody, because the search is what reaches a project past the first
          page, and a client with sixty projects needs that as much as we do. */}
      <Card sx={{ p: "12px 14px", mb: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-end">
          {isBackOffice && clients.length > 0 ? (
            <ClientSelect
              clients={clients}
              value={clientFilter}
              onChange={setClientFilter}
              emptyLabel="All clients"
              sx={{ minWidth: 220 }}
            />
          ) : null}

          {/* Whose projects, which is the other half of "which projects". Set
              alongside "My projects" below rather than instead of it: one is
              somebody else's list and the other is one click. */}
          <PersonSelect
            people={people}
            onSearch={searchPeople}
            label="Owner"
            value={ownerFilter}
            onChange={(id) => {
              setOwnerFilter(id)
              // Both set, they are two answers to one question and the list
              // would come back empty for no visible reason.
              if (id) setMine(false)
            }}
            emptyLabel="Anyone"
            sx={{ minWidth: 200 }}
          />

          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or code"
            sx={{ minWidth: 220 }}
          />

          {/*
            * Three sentences, because they mean different things.
            *
            * Filtered: how many matched, out of how many there are. Unfiltered
            * and complete: just the number. Unfiltered and truncated: the one
            * that matters — there are more than this, and here is how to reach
            * them. Coloured, because a list quietly hiding a quarter of itself
            * is the thing somebody needs to notice.
            */}
          <Typography
            variant="caption"
            sx={{ pb: 0.5 }}
            color={!filtersActive && total !== null && projects.length < total ? "warning.dark" : "text.secondary"}
          >
            {filtersActive
              ? `${total ?? projects.length} of ${overall ?? total ?? projects.length} project${(overall ?? total) === 1 ? "" : "s"}` +
                (total !== null && projects.length < total ? ` — showing the first ${projects.length}` : "")
              : total !== null && projects.length < total
                ? `${projects.length} of ${total} projects — search to find the rest`
                : `${projects.length} project${projects.length === 1 ? "" : "s"}`}
          </Typography>
        </Stack>

        {/* A rule between the two rows: above narrows by what a project is,
            below by whose it is. */}
        <Divider sx={{ my: 1.25 }} />

        {/* Views, Clear and the count sit down here with the toggles rather
            than trailing the selects: after a flex spacer they wrap with the
            row, and a count stranded under the first field reads as a caption
            belonging to it. Same arrangement as the task list. */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <QuickFilters
            items={PROJECT_VIEWS}
            values={{ mine }}
            onToggle={() => {
              setMine((on) => !on)
              // Same question as the Owner box, so they cannot both be answered.
              setOwnerFilter("")
            }}
          />

          <Box sx={{ flex: 1 }} />

          {/* This page keeps its filters in four separate pieces of state, so
              they are gathered into one object here rather than stored that
              way — a view has to be a single thing to save and to hand back. */}
          <SavedViews
            page="projects"
            filters={{ client_id: clientFilter, owner_id: ownerFilter, mine, q: search }}
            defaults={PROJECT_FILTER_DEFAULTS}
            onApply={(saved) => {
              setClientFilter(saved.client_id || "")
              setOwnerFilter(saved.owner_id || "")
              setMine(Boolean(saved.mine))
              setSearch(saved.q || "")
            }}
          />

          {filtersActive ? (
            <Button size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : null}
        </Stack>
      </Card>

      {loading ? (
        <LoadingRows rows={4} height={130} />
      ) : projects.length === 0 ? (
        <Card>
          {/* A search that found nothing and an account with no projects at all
              are different situations, and offering "New project" to somebody
              who has just mistyped a code is an answer to a question they did
              not ask. */}
          <EmptyState
            icon={FolderIcon}
            title={filtersActive ? "Nothing matches" : "No projects"}
            description={
              filtersActive
                ? "No project matches this search. Clear the filters to see them all."
                : "A project groups tasks, the people working on them, and the conversations about them."
            }
            actionLabel={filtersActive ? "Clear filters" : can("projects", "create") ? "New project" : null}
            onAction={
              filtersActive
                ? () => {
                    setClientFilter("")
                    setSearch("")
                  }
                : () => setCreateOpen(true)
            }
          />
        </Card>
      ) : (
        <Grid container spacing={2}>
          {projects.map((project, index) => {
            const total = Number(project.task_count) || 0
            const open = Number(project.open_task_count) || 0
            const done = total - open
            const progress = total > 0 ? Math.round((done / total) * 100) : 0
            /* One sentence covering all four cases, rather than two fields that
               are usually both blank. "No dates set" is a fact worth saying: a
               project nobody has scheduled is the one that slips. */
            const overdue = isOverdue(project.due_date) && project.status !== "completed" && project.status !== "archived"
            const span =
              project.start_date && project.due_date
                ? `${formatDate(project.start_date, "MMM D")} → ${formatDate(project.due_date, "MMM D, YYYY")}`
                : project.due_date
                  ? `Due ${formatDate(project.due_date)}`
                  : project.start_date
                    ? `Started ${formatDate(project.start_date)}`
                    : "No dates set"

            return (
              <Grid key={project.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card sx={{ height: "100%", ...INTERACTIVE_CARD, ...rise(index) }}>
                  <CardActionArea onClick={() => navigate(`/projects/${project.id}`)} sx={{ height: "100%" }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip
                          size="small"
                          label={project.code}
                          sx={{
                            height: 20,
                            fontFamily: "monospace",
                            fontSize: 11,
                            ...filledChipSx(project.color, BRAND_FILL),
                          }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Chip size="small" label={project.status.replace("_", " ")} color={STATUS_COLOR[project.status]} sx={{ height: 20, fontSize: 11 }} />
                        {/* Reusing a project is something people do from the
                            list, where they are looking at the one they want to
                            reuse — not after opening it. */}
                        {can("projects", "create") ? (
                          <Tooltip title="Duplicate this project">
                            <IconButton
                              size="small"
                              aria-label={`Duplicate ${project.name}`}
                              onClick={(event) => {
                                event.preventDefault()
                                event.stopPropagation()
                                setCopying(project)
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <CopyIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>

                      {/*
                        * The client, above the name rather than greyed out under it.
                        *
                        * Scanning a grid of projects, "whose is this" is asked before
                        * "what is it called" \u2014 and two clients both have an
                        * "Onboarding". As a caption underneath it was the quietest
                        * thing on the card; as an eyebrow it is the first.
                        *
                        * Weight and position rather than colour, because the brand
                        * green is about 1.8:1 on white and the card has to read the
                        * same in both themes. Back-office projects say "Internal" so
                        * the slot is never empty and the cards stay aligned.
                        */}
                      <Typography
                        variant="caption"
                        display="block"
                        noWrap
                        sx={{ fontWeight: 700, letterSpacing: "0.04em", color: "text.primary", mb: 0.25 }}
                      >
                        {project.client_id === "0" ? "Internal" : project.client_label || "Internal"}
                      </Typography>

                      <Typography variant="h4" gutterBottom noWrap>
                        {project.name}
                      </Typography>

                      {project.description ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                        >
                          {project.description}
                        </Typography>
                      ) : null}

                      {/* Only when there is something to measure: a 0% bar on a
                          project with no tasks says "behind", not "empty". */}
                      {total > 0 ? (
                        <Box sx={{ mb: 1.5 }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {done} of {total} done
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {progress}%
                            </Typography>
                          </Stack>
                          <LinearProgress variant="determinate" value={progress} sx={{ height: 5, borderRadius: 3 }} />
                        </Box>
                      ) : (
                        /* Something in the slot, or the card is visibly shorter
                           than the ones beside it and reads as cut off. Still no
                           0% bar: an empty bar says "behind", and a project
                           nobody has scheduled work on yet is not behind. */
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: "14px", mb: 1.5 }}>
                          No tasks yet
                        </Typography>
                      )}

                      {/*
                        * Who owns it. A project with nobody's name on it is one
                        * nobody chases, and "who do I ask about this" was a question
                        * the card could not answer without opening it.
                        */}
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Avatar
                          sx={{
                            width: 22,
                            height: 22,
                            fontSize: 10,
                            fontWeight: 700,
                            bgcolor: project.owner_name ? avatarColor(project.owner_name) : "action.disabledBackground",
                            color: project.owner_name ? "#fff" : "text.disabled",
                          }}
                        >
                          {project.owner_name ? initialsOf(project.owner_name) : "?"}
                        </Avatar>
                        <Typography variant="caption" color={project.owner_name ? "text.primary" : "text.secondary"} noWrap>
                          {project.owner_name || "No owner"}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {project.member_count} member{project.member_count === 1 ? "" : "s"}
                        </Typography>
                      </Stack>

                      {/*
                        * The dates as one line, because a project's dates are a span
                        * and reading them apart is doing the subtraction yourself.
                        * Overdue is said in words as well as colour \u2014 the colour
                        * alone is invisible to anybody who cannot see it, and on a
                        * card this small it is four characters of red.
                        */}
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <CalendarIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                        <Typography
                          variant="caption"
                          color={overdue ? "error.main" : "text.secondary"}
                          fontWeight={overdue ? 650 : 400}
                          noWrap
                        >
                          {span}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      )}

      <CopyProjectDialog project={copying} onClose={() => setCopying(null)} onCopied={() => setCopying(null)} />

      <ProjectDialog
        open={createOpen}
        clients={clients}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false)
          load()
        }}
      />
    </Box>
  )
}
